import React, { PropsWithChildren, PureComponent } from 'react';
import produce from 'immer';
import { QuestionnaireResponseContext } from '../../../contexts/questionnaireResponse';
import { IQuestionnaire } from '../../../modules/unsw-fhir/Questionnaire';
import { IQuestionnaireResponse } from '../../../modules/unsw-fhir/QuestionnaireResponse';
import { getQuestionnaireByReference } from '../../../apis/questionnaire';
import {
  getQuestionnaireResponseById,
  getQuestionnaireResponseByReference,
} from '../../../apis/questionnaireResponse';
import { isQuestionnaireEqualById } from '../../../modules/unsw-fhir/Reference';

type QuestionnaireResponseProviderProps = PropsWithChildren<{
  questionnaireResponseId?: string;
  questionnaireId?: string;
  patientId?: string;
  questionnaireResponse?: IQuestionnaireResponse;
}>;

class QuestionnaireResponseProvider extends PureComponent<
  QuestionnaireResponseProviderProps,
  QuestionnaireResponseContext & {
    questionnaireResponseId?: string;
  }
> {
  constructor(props: QuestionnaireResponseProviderProps) {
    super(props);
    this.state = {
      setQuestionnaireResponse: this.setQuestionnaireResponse,
    };
  }

  componentDidMount = async () => {
    await this.updateState();
  };

  componentDidUpdate = async (pProps: QuestionnaireResponseProviderProps) => {
    if (
      pProps.questionnaireResponseId !== this.props.questionnaireResponseId ||
      pProps.questionnaireId !== this.props.questionnaireId ||
      pProps.patientId !== this.props.patientId
    ) {
      await this.updateState();
      return;
    }
  };
  render = () => {
    return (
      <QuestionnaireResponseContext.Provider value={this.state}>
        {this.props.children}
      </QuestionnaireResponseContext.Provider>
    );
  };

  private isQuestionnaireEqual(
    questionnaire?: IQuestionnaire,
    questionnaireResponse?: IQuestionnaireResponse,
  ) {
    if (!questionnaire && questionnaireResponse) return false;
    if (
      questionnaireResponse &&
      questionnaire &&
      isQuestionnaireEqualById(
        questionnaireResponse.questionnaire,
        questionnaire.id,
      )
    )
      return true;
  }

  private setQuestionnaireResponse = (
    questionnaireResponse?: IQuestionnaireResponse,
  ) => {
    this.setState(state =>
      produce(state, draft => {
        draft.questionnaireResponse = questionnaireResponse;
        return draft;
      }),
    );
  };

  private updateState = async () => {
    try {
      this.setState(state =>
        produce(state, draft => {
          draft.questionnaireResponseLoading = true;
          draft.questionnaireLoading = this.isQuestionnaireEqual(
            this.state.questionnaire,
            this.state.questionnaireResponse,
          );
          return draft;
        }),
      );

      const questionnaireResponse = this.props.questionnaireResponseId
        ? await getQuestionnaireResponseById(this.props.questionnaireResponseId)
        : this.props.questionnaireId && this.props.patientId
        ? await getQuestionnaireResponseByReference(
            this.props.patientId,
            this.props.questionnaireId,
          )
        : undefined;

      this.setState(state =>
        produce(state, draft => {
          draft.questionnaireResponseLoading = false;
          draft.questionnaireResponse = questionnaireResponse;
          return draft;
        }),
      );

      let questionnaire = this.state.questionnaire;
      if (
        !this.isQuestionnaireEqual(
          this.state.questionnaire,
          questionnaireResponse,
        )
      ) {
        questionnaire = this.state.questionnaireResponse
          ? await getQuestionnaireByReference(
              this.state.questionnaireResponse.questionnaire,
            )
          : undefined;
      }

      this.setState(state =>
        produce(state, draft => {
          draft.questionnaire = questionnaire;
          draft.questionnaireLoading = false;
          return draft;
        }),
      );
    } catch (e) {
      this.setState(state =>
        produce(state, draft => {
          draft.questionnaireResponseLoading = false;
          draft.questionnaireLoading = false;
          draft.error = e as string | undefined;
          return draft;
        }),
      );
    }
  };
}

export default QuestionnaireResponseProvider;
